import moment from "moment";

import { InfoItem, Section } from "features/pregnancy/components/ConsultationLayout";
import { useBabyObservations } from "features/pregnancy/hooks/useBabyObservations";

import style from "./PregnancyPostNatalConsultationContent.module.css";

const feedingLabel = (observation) => {
  if (observation.breastFeeding && observation.formulaFeeding) {
    return "Breast and Formula combination";
  }
  if (observation.formulaFeeding) return "Formula";
  if (observation.breastFeeding) return "Breast";

  return null;
};

const crtLabel = (crt) => {
  if (crt === "yes") return "Normal (<2s)";
  if (crt === "no") return "Prolonged (≥2s)";

  return crt;
};

// One baby's Subject + Objective, read-only (KIN-1075). `suffix` labels the baby
// (e.g. " — Baby A") when the pregnancy is multiple.
const BabyObservationSections = ({ observation, suffix = "" }) => {
  const bornAt = observation.bornAt
    ? moment(observation.bornAt).format("DD.MM.YYYY")
    : null;
  const deliveryMethod = observation.deliveryMethod
    ? observation.deliveryMethod.replace(/-/g, " ")
    : null;

  return (
    <>
      <Section title={`Subject${suffix}`}>
        <InfoItem label="Born at" value={bornAt} />
        <InfoItem label="Delivery method" value={deliveryMethod} />
        <InfoItem
          label="Weight at birth"
          value={observation.weightAtBirthGrams ? `${observation.weightAtBirthGrams} g` : null}
        />
        <InfoItem label="Feeding" value={feedingLabel(observation)} />
        <InfoItem
          label="Jaundice"
          value={observation.jaundice != null ? observation.jaundice : null}
        />
        <InfoItem label="Issues of note" value={observation.issuesOfNote} />
        <InfoItem label="PHN Visit" value={observation.phnVisit} />
      </Section>

      <Section title={`Objective${suffix}`}>
        <InfoItem label="Weight" value={observation.weightInG ? `${observation.weightInG} g` : null} />
        <InfoItem label="Length" value={observation.lengthInCm ? `${observation.lengthInCm} cm` : null} />
        <InfoItem
          label="Head circumference"
          value={observation.headCircumferenceInCm ? `${observation.headCircumferenceInCm} cm` : null}
        />
        <InfoItem label="CRT" value={crtLabel(observation.crt)} />
        <InfoItem label="Umbilicus" value={observation.umbilicus} />
        <InfoItem label="Barlow and Ortolani's Test" value={observation.barlowAndOrtolaniTest} />
        <InfoItem label="Heart" value={observation.heart} />
        <InfoItem label="Fontanelles" value={observation.fontanelles} />
        <InfoItem label="Buccal mucosa" value={observation.buccalMucosa} />
        <InfoItem label="Feet" value={observation.feet} />
        <InfoItem label="Impression" value={observation.impression} />
        <InfoItem label="Plan" value={observation.plan} />
      </Section>
    </>
  );
};

// Legacy single-baby view from the consultation's inline fields, used when a
// consultation has no per-baby observations yet (pre-KIN-1075 records).
const LegacyBabySections = ({ subject, objective }) => {
  const bornAt = subject.bornAt ? moment(subject.bornAt).format("DD.MM.YYYY") : null;
  const deliveryMethod = subject.deliveryMethod
    ? subject.deliveryMethod.replace(/-/g, " ")
    : null;

  return (
    <>
      <Section title="Subject">
        <InfoItem label="Born at" value={bornAt} />
        <InfoItem label="Delivery method" value={deliveryMethod} />
        <InfoItem label="Weight at birth" value={subject.weightAtBirth ? `${subject.weightAtBirth} g` : null} />
        <InfoItem label="Feeding" value={subject.feeding} />
        <InfoItem label="Jaundice" value={subject.jaundice != null ? (subject.jaundice === "Yes" ? "Yes" : "No") : null} />
        <InfoItem label="Issues of note" value={subject.issuesOfNote} />
        <InfoItem label="PHN Visit" value={subject.phnVisit} />
      </Section>

      <Section title="Objective">
        <InfoItem label="Weight" value={objective.weight ? `${objective.weight} g` : null} />
        <InfoItem label="Length" value={objective.length ? `${objective.length} cm` : null} />
        <InfoItem label="Head circumference" value={objective.headCircumference ? `${objective.headCircumference} cm` : null} />
        <InfoItem label="CRT" value={crtLabel(objective.CRT)} />
        <InfoItem label="Umbilicus" value={objective.umbilicus} />
        <InfoItem label="Barlow and Ortolani's Test" value={objective.barlowAndOrtolaniTest} />
        <InfoItem label="Heart" value={objective.heart} />
        <InfoItem label="Fontanelles" value={objective.fontanelles} />
        <InfoItem label="Buccal mucosa" value={objective.buccalMucosa} />
        <InfoItem label="Feet" value={objective.feet} />
        <InfoItem label="Impression" value={objective.impression} />
        <InfoItem label="Plan" value={objective.plan} />
      </Section>
    </>
  );
};

export const PregnancyPostNatalConsultationContent = ({ consultation }) => {
  const subject = consultation.subject ?? {};
  const objective = consultation.objective ?? {};
  const mother = consultation.mother ?? {};

  const { data: babyObservations } = useBabyObservations(
    consultation.id ? Number(consultation.id) : null
  );
  const hasObservations = (babyObservations?.length ?? 0) > 0;
  const isMultiple = (babyObservations?.length ?? 0) > 1;

  return (
    <div className={style.contentRoot}>
      <div className={style.sectionFirst}>
        <p className={style.sectionTitle}>Doctor</p>
        <p className={style.singleValue}>{consultation.doctor || "-"}</p>
      </div>

      {hasObservations ? (
        babyObservations.map((observation) => (
          <BabyObservationSections
            key={observation.id}
            observation={observation}
            suffix={
              isMultiple && observation.foetusIdentifier
                ? ` — Baby ${observation.foetusIdentifier}`
                : ""
            }
          />
        ))
      ) : (
        <LegacyBabySections subject={subject} objective={objective} />
      )}

      <Section title="Mother">
        <InfoItem label="Gravida" value={mother.gravida} />
        <InfoItem label="Para" value={mother.para} />
        <InfoItem label="Issues" value={mother.issuesDuringOrSincePregnancy} />
        <InfoItem label="Post-natal mood" value={mother.postNatalMood} />
        <InfoItem label="Supports" value={mother.supports} />
        <InfoItem label="Contraception" value={mother.contraception} />
        <InfoItem label="Mirena" value={mother.mirena} />
      </Section>
    </div>
  );
};
